import React, { useEffect, useMemo, useState } from 'react';
import { TIERS } from './scoring.mjs';
import { MAX_NAME_LENGTH } from './playerName.mjs';
import { dayIso, formatDay } from './daily.mjs';

/*
 * The board is one day's game by default, because that is what the day's game
 * is for: everyone gets the same three places, so the only board on which the
 * numbers mean anything against each other is a single day's. The all-time view
 * is still here, one option along, but it ranks games that were never the same
 * game and it should not be what opens.
 */
const ALL_TIME = 'all';

/*
 * `submission` and `onSubmitScore` arrive only on the results screen, where the
 * board is the thing a score is being added to — putting the name field on the
 * board it joins reads better than leaving it stranded under the summary map.
 * The intro page renders the same component without them and gets no form.
 */
export default function Leaderboard({
  tier,
  onChangeTier,
  refreshKey,
  submission,
  onSubmitScore,
  onChangeName,
  rememberedName = '',
  playedTier
}) {
  const [state, setState] = useState({ status: 'loading', games: [] });
  /*
   * Seeded from the remembered name, so the retry after a failed automatic
   * submission is a button press rather than a retype.
   */
  const [name, setName] = useState(rememberedName);
  const [editing, setEditing] = useState(false);

  /*
   * Today by the player's own clock, which is the same thing the day's rounds
   * were generated from — so this names the board the score just went onto.
   */
  const today = dayIso();
  const [day, setDay] = useState(today);
  const [days, setDays] = useState([]);

  const submit = (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed) onSubmitScore(trimmed);
  };

  /*
   * Changing the name after the fact does *not* resubmit. The score is already
   * on the board, nothing on the server merges a second POST with the first, and
   * "fix my name" turning into two entries would be a worse bug than the typo.
   * So this only changes what the next game posts under — and the copy says so
   * rather than leaving it to be discovered.
   */
  const saveName = (event) => {
    event.preventDefault();
    onChangeName(name.trim());
    setEditing(false);
  };

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', games: [] });

    const on = day === ALL_TIME ? '' : `&day=${day}`;
    fetch(`/api/starguessr/leaderboard?difficulty=${tier}&limit=20${on}`)
      .then((response) => {
        if (!response.ok) throw new Error('Could not load the leaderboard');
        return response.json();
      })
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', games: data.games || [] });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', games: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [tier, day, refreshKey]);

  /*
   * Which days have anything on them. The picker walks this rather than the
   * calendar, so it never lands on a day nobody played and a gap is skipped
   * instead of being offered as an empty board.
   *
   * A failure here is not worth reporting: the board itself still loads, and the
   * result is a picker holding only today, which is where it opens anyway.
   */
  useEffect(() => {
    let cancelled = false;

    fetch(`/api/starguessr/days?difficulty=${tier}`)
      .then((response) => (response.ok ? response.json() : { days: [] }))
      .then((data) => {
        if (!cancelled) setDays((data.days || []).map((entry) => entry.day).filter(Boolean));
      })
      .catch(() => {
        if (!cancelled) setDays([]);
      });

    return () => {
      cancelled = true;
    };
  }, [tier, refreshKey]);

  /*
   * A submitted score pulls the view back to today, because the score that was
   * just posted is on today's board and looking at March while being told the
   * game was saved is a puzzle nobody should have to solve.
   */
  useEffect(() => {
    if (refreshKey) setDay(dayIso());
  }, [refreshKey]);

  /*
   * Today is always offered even before anyone has played it — an empty board
   * for the day in progress is the honest answer, not a missing option. Sorted
   * rather than prepended because a player far enough east can post a day this
   * browser has not reached yet.
   */
  const options = useMemo(
    () => Array.from(new Set([today, ...days])).sort().reverse(),
    [days, today]
  );

  const at = options.indexOf(day);
  const older = at >= 0 && at < options.length - 1 ? options[at + 1] : null;
  const newer = at > 0 ? options[at - 1] : null;

  return (
    <section className="sg-board">
      <div className="sg-board-head">
        <h2>Leaderboard</h2>
        <div className="sg-tier-toggle" role="group" aria-label="Leaderboard tier">
          {Object.values(TIERS).map((entry) => (
            <button
              key={entry.key}
              type="button"
              className={entry.key === tier ? 'is-active' : ''}
              onClick={() => onChangeTier(entry.key)}
            >
              {entry.label}
            </button>
          ))}
        </div>
      </div>

      {/*
        Arrows for the common move — one day back, and back again — and the
        select for the rest, including the way out to the all-time board. Both
        walk the same list, so neither can reach a day the other cannot.
      */}
      <div className="sg-board-days">
        <button
          type="button"
          className="sg-day-step"
          onClick={() => older && setDay(older)}
          disabled={!older}
          aria-label="Earlier day"
        >
          &lsaquo;
        </button>

        <select
          className="sg-day-pick"
          value={day}
          onChange={(event) => setDay(event.target.value)}
          aria-label="Which day to rank"
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {option === today ? 'Today' : formatDay(option)}
            </option>
          ))}
          <option value={ALL_TIME}>All time</option>
        </select>

        <button
          type="button"
          className="sg-day-step"
          onClick={() => newer && setDay(newer)}
          disabled={!newer}
          aria-label="Later day"
        >
          &rsaquo;
        </button>
      </div>

      {/*
        Four states, and which one is showing is the whole explanation of what
        just happened: saved (with the name it went up under), saving, changing
        that name for next time, or a form because there is nothing remembered
        yet — or because the automatic attempt failed.
      */}
      {onSubmitScore &&
        (submission.status === 'done' && !editing ? (
          <p className="sg-submitted">
            Saved to the {TIERS[playedTier].label} board
            {submission.name ? (
              <>
                {' as '}
                <strong>{submission.name}</strong>
              </>
            ) : null}
            .{' '}
            <button type="button" className="sg-name-change" onClick={() => setEditing(true)}>
              Change name
            </button>
            {/*
              Only when the two have parted company — which is exactly the moment
              someone has just changed the name and needs telling that this score
              kept the old one.
            */}
            {rememberedName !== submission.name && (
              <span className="sg-name-note">
                {rememberedName
                  ? <>Your next game will post as <strong>{rememberedName}</strong>.</>
                  : 'Scores will no longer be submitted automatically.'}
              </span>
            )}
          </p>
        ) : editing ? (
          <form className="sg-submit" onSubmit={saveName}>
            <input
              type="text"
              value={name}
              maxLength={MAX_NAME_LENGTH}
              placeholder="Name for the leaderboard"
              aria-label="Name for the leaderboard"
              onChange={(event) => setName(event.target.value)}
            />
            <button type="submit" className="sg-primary">
              Save name
            </button>
            {/*
              An empty name is the way out: it stops the next game posting by
              itself, which is the only opt-out this needs.
            */}
            <p className="sg-name-note">
              Used from your next game
              {submission.name ? <>; this score stays on the board as <strong>{submission.name}</strong></> : ''}.
              Clear the box to stop scores being submitted automatically.
            </p>
          </form>
        ) : (
          <form className="sg-submit" onSubmit={submit}>
            <input
              type="text"
              value={name}
              maxLength={MAX_NAME_LENGTH}
              placeholder="Name for the leaderboard"
              aria-label="Name for the leaderboard"
              onChange={(event) => setName(event.target.value)}
            />
            <button type="submit" className="sg-primary" disabled={!name.trim() || submission.status === 'saving'}>
              {submission.status === 'saving' ? 'Saving…' : 'Submit score'}
            </button>
            {submission.error && <p className="sg-error">{submission.error}</p>}
          </form>
        ))}

      {state.status === 'loading' && <p className="sg-muted">Loading…</p>}
      {state.status === 'error' && <p className="sg-muted">The leaderboard is not reachable right now.</p>}
      {state.status === 'ready' && state.games.length === 0 && (
        <p className="sg-muted">
          {day === ALL_TIME
            ? 'No scores on this board yet.'
            : day === today
              ? 'Nobody has finished today’s game yet.'
              : 'Nobody played this one.'}
        </p>
      )}

      {state.games.length > 0 && (
        <ol className="sg-board-list">
          {state.games.map((game, index) => (
            <li key={game.id}>
              <span className="sg-board-rank">{index + 1}</span>
              <span className="sg-board-name">{game.playerName}</span>
              <span className="sg-board-score">{game.totalScore.toLocaleString()}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
